import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Business, Close, Groups, HourglassTop, Repeat, Block } from '@mui/icons-material';
import { Grid } from '../components/common/TypedGrid';
import {
  EmptyState,
  FilterTabs,
  PageHeader,
  ResponsiveTable,
  ResponsiveColumn,
  SearchField,
  SectionCard,
  StatCard,
  StatusChip,
} from '../components/common';
import { useAdmin } from '../contexts/AdminContext';
import agencyAdminService, {
  AdminAgency,
  AdminSubscription,
  AgencyKyb,
  AgencyMemberRow,
  callableError,
} from '../services/agencyAdminService';

type Filter = 'pending' | 'approved' | 'suspended' | 'rejected' | 'all';
type Decision = 'approve' | 'reject' | 'suspend' | 'reactivate';

const rupees = (n?: number) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const masked = (acct?: string) => (acct ? `•••• ${acct.slice(-4)}` : '—');

/**
 * Agencies: businesses that employ partners as staff. Admins review new
 * applications (business, services, bank details), and can suspend an agency
 * or bring it back. Everything else — staff, bookings, dispatch — the agency
 * runs itself from the mobile app.
 */
const Agencies: React.FC = () => {
  const { effectiveCityId } = useAdmin();
  const [agencies, setAgencies] = useState<AdminAgency[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('pending');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminAgency | null>(null);
  const [kyb, setKyb] = useState<AgencyKyb | null>(null);
  const [members, setMembers] = useState<AgencyMemberRow[]>([]);
  const [subs, setSubs] = useState<AdminSubscription[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pendingDecision, setPendingDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAgencies(await agencyAdminService.getAgencies(effectiveCityId));
    } catch (e) {
      setToast({ msg: callableError(e), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [effectiveCityId]);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (a: AdminAgency) => {
    setSelected(a);
    setDetailLoading(true);
    try {
      const [k, m, s] = await Promise.all([
        agencyAdminService.getKyb(a.id),
        agencyAdminService.getMembers(a.id),
        agencyAdminService.getAgencySubscriptions(a.id),
      ]);
      setKyb(k);
      setMembers(m);
      setSubs(s);
    } finally {
      setDetailLoading(false);
    }
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = { pending: 0, approved: 0, suspended: 0, rejected: 0 };
    agencies.forEach((a) => (c[a.verificationStatus] = (c[a.verificationStatus] || 0) + 1));
    return c;
  }, [agencies]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agencies.filter(
      (a) =>
        (filter === 'all' || a.verificationStatus === filter) &&
        (!q || a.name.toLowerCase().includes(q) || a.ownerName.toLowerCase().includes(q) || a.phone.includes(q))
    );
  }, [agencies, filter, search]);

  const decide = async () => {
    if (!selected || !pendingDecision) return;
    if ((pendingDecision === 'reject' || pendingDecision === 'suspend') && !reason.trim()) return;
    setActing(true);
    try {
      await agencyAdminService.review(selected.id, pendingDecision, reason.trim() || undefined);
      setToast({
        msg: { approve: 'Agency approved — the owner can now open the agency console', reject: 'Sent back with your reason', suspend: 'Agency suspended', reactivate: 'Agency reactivated' }[pendingDecision],
        severity: 'success',
      });
      setPendingDecision(null);
      setReason('');
      setSelected(null);
      await load();
    } catch (e) {
      setToast({ msg: callableError(e), severity: 'error' });
    } finally {
      setActing(false);
    }
  };

  const columns: ResponsiveColumn<AdminAgency>[] = [
    {
      key: 'name',
      header: 'Agency',
      mobile: 'title',
      render: (a) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>{a.name.charAt(0).toUpperCase()}</Avatar>
          <Box>
            <Typography fontWeight={700}>{a.name}</Typography>
            <Typography variant="caption" color="text.secondary">{a.ownerName} · {a.phone}</Typography>
          </Box>
        </Stack>
      ),
    },
    { key: 'city', header: 'City', mobile: 'subtitle', render: (a) => a.cityName || '—' },
    {
      key: 'services',
      header: 'Services',
      mobile: 'body',
      render: (a) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {a.segmentNames.slice(0, 3).map((s) => <Chip key={s} label={s} size="small" variant="outlined" />)}
          {a.segmentNames.length > 3 && <Chip label={`+${a.segmentNames.length - 3}`} size="small" />}
        </Stack>
      ),
    },
    { key: 'team', header: 'Team', align: 'center', mobile: 'body', render: (a) => a.staffCount ?? 1 },
    { key: 'subs', header: 'Subscriptions', align: 'center', mobile: 'body', render: (a) => a.activeSubscriptions ?? 0 },
    { key: 'earned', header: 'Earned', align: 'right', mobile: 'body', render: (a) => rupees(a.totalEarnings) },
    { key: 'status', header: 'Status', mobile: 'actions', render: (a) => <StatusChip status={a.verificationStatus} /> },
  ];

  const s = selected;
  const decisionCopy: Record<Decision, { title: string; body: string; cta: string; color: 'primary' | 'error' | 'success' }> = {
    approve: { title: `Approve ${s?.name}?`, body: 'The owner gets the agency console and can invite staff and take bookings.', cta: 'Approve', color: 'success' },
    reject: { title: `Send ${s?.name} back?`, body: 'Tell them what to fix — they see this and can resubmit.', cta: 'Send back', color: 'error' },
    suspend: { title: `Suspend ${s?.name}?`, body: 'They keep their data but can’t accept new bookings or invite staff. Existing visits are not cancelled.', cta: 'Suspend', color: 'error' },
    reactivate: { title: `Reactivate ${s?.name}?`, body: 'They can take bookings again.', cta: 'Reactivate', color: 'primary' },
  };

  return (
    <Box>
      <PageHeader title="Agencies" subtitle="Businesses that run a team of partners — review applications and keep an eye on how they operate." />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><StatCard label="Awaiting review" value={counts.pending || 0} icon={<HourglassTop />} tone="warning" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Approved" value={counts.approved || 0} icon={<Business />} tone="success" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="People in agency teams" value={agencies.reduce((n, a) => n + (a.staffCount || 0), 0)} icon={<Groups />} tone="info" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Active subscriptions" value={agencies.reduce((n, a) => n + (a.activeSubscriptions || 0), 0)} icon={<Repeat />} tone="primary" /></Grid>
      </Grid>

      <SectionCard disablePadding>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ p: 2 }}>
          <FilterTabs<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'pending', label: 'Pending', count: counts.pending },
              { value: 'approved', label: 'Approved', count: counts.approved },
              { value: 'suspended', label: 'Suspended', count: counts.suspended },
              { value: 'rejected', label: 'Rejected', count: counts.rejected },
              { value: 'all', label: 'All', count: agencies.length },
            ]}
          />
          <SearchField value={search} onChange={setSearch} placeholder="Search name, owner or phone" />
        </Stack>
        <ResponsiveTable<AdminAgency>
          columns={columns}
          rows={rows}
          rowKey={(a) => a.id}
          loading={loading}
          onRowClick={open}
          emptyTitle={filter === 'pending' ? 'No agencies waiting for review' : 'No agencies here'}
          emptyDescription="Agencies register from the PickupNinja app under “Become a partner”."
        />
      </SectionCard>

      <Drawer anchor="right" open={!!s} onClose={() => setSelected(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
        {s && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 52, height: 52, fontSize: 22 }}>{s.name.charAt(0).toUpperCase()}</Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={800}>{s.name}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <StatusChip status={s.verificationStatus} size="small" />
                  {s.cityName && <Typography variant="caption" color="text.secondary">{s.cityName}</Typography>}
                </Stack>
              </Box>
              <IconButton onClick={() => setSelected(null)}><Close /></IconButton>
            </Stack>

            {(s.rejectionReason || s.suspensionReason) && (
              <Alert severity={s.verificationStatus === 'suspended' ? 'error' : 'warning'} sx={{ mt: 2 }}>
                {s.verificationStatus === 'suspended' ? `Suspended: ${s.suspensionReason || '—'}` : `Sent back: ${s.rejectionReason}`}
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              {s.verificationStatus === 'pending' && (
                <>
                  <Button variant="contained" color="success" onClick={() => setPendingDecision('approve')}>Approve</Button>
                  <Button variant="outlined" color="error" onClick={() => setPendingDecision('reject')}>Send back</Button>
                </>
              )}
              {s.verificationStatus === 'approved' && (
                <Button variant="outlined" color="error" startIcon={<Block />} onClick={() => setPendingDecision('suspend')}>Suspend</Button>
              )}
              {s.verificationStatus === 'suspended' && (
                <Button variant="contained" onClick={() => setPendingDecision('reactivate')}>Reactivate</Button>
              )}
            </Stack>

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="overline" color="text.secondary">Business</Typography>
            {[
              ['Owner', `${s.ownerName} · ${s.phone}`],
              ['Email', s.email || '—'],
              ['Address', s.address],
              ['Services', s.segmentNames.join(', ') || '—'],
              ['About', s.about || '—'],
            ].map(([k, v]) => (
              <Stack key={k} direction="row" spacing={2} sx={{ py: 0.75 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>{k}</Typography>
                <Typography variant="body2">{v}</Typography>
              </Stack>
            ))}

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="overline" color="text.secondary">Verification & payouts</Typography>
            {detailLoading ? (
              <CircularProgress size={22} sx={{ display: 'block', my: 2 }} />
            ) : (
              [
                ['GST', kyb?.gstNumber || '—'],
                ['Registration', kyb?.registrationNumber || '—'],
                ['Account holder', kyb?.accountHolderName || '—'],
                ['Account', masked(kyb?.bankAccountNumber)],
                ['IFSC', kyb?.ifscCode || '—'],
              ].map(([k, v]) => (
                <Stack key={k} direction="row" spacing={2} sx={{ py: 0.75 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>{k}</Typography>
                  <Typography variant="body2" fontFamily={k === 'IFSC' || k === 'Account' ? 'monospace' : undefined}>{v}</Typography>
                </Stack>
              ))
            )}

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="overline" color="text.secondary">Team ({members.filter((m) => m.status === 'active').length})</Typography>
            <List dense disablePadding>
              {members.map((m) => (
                <ListItem key={m.uid} disableGutters sx={{ opacity: m.status === 'active' ? 1 : 0.5 }}>
                  <ListItemAvatar><Avatar sx={{ width: 32, height: 32 }}>{m.name.charAt(0)}</Avatar></ListItemAvatar>
                  <ListItemText primary={m.name} secondary={`${m.role}${m.phone ? ` · ${m.phone}` : ''}${m.status !== 'active' ? ' · removed' : ''}`} />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="overline" color="text.secondary">Subscriptions ({subs.length})</Typography>
            {subs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>None yet.</Typography>
            ) : (
              <List dense disablePadding>
                {subs.slice(0, 20).map((x) => (
                  <ListItem key={x.id} disableGutters secondaryAction={<StatusChip status={x.status} size="small" />}>
                    <ListItemText primary={`${x.serviceName} · ${x.scheduleLabel || x.schedule.frequency} ${x.schedule.startTime}`} secondary={`${x.customerInfo?.name || 'Customer'} · ${x.assigneeName || '—'} · ${x.visitsCompleted || 0} done`} />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </Drawer>

      <Dialog open={!!pendingDecision} onClose={() => !acting && setPendingDecision(null)} maxWidth="xs" fullWidth>
        {pendingDecision && (
          <>
            <DialogTitle>{decisionCopy[pendingDecision].title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{decisionCopy[pendingDecision].body}</Typography>
              {(pendingDecision === 'reject' || pendingDecision === 'suspend') && (
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={2}
                  label="Reason (shown to the agency)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingDecision(null)} disabled={acting}>Cancel</Button>
              <Button
                variant="contained"
                color={decisionCopy[pendingDecision].color}
                onClick={decide}
                disabled={acting || ((pendingDecision === 'reject' || pendingDecision === 'suspend') && !reason.trim())}
              >
                {acting ? <CircularProgress size={20} color="inherit" /> : decisionCopy[pendingDecision].cta}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>

      {!loading && agencies.length === 0 && filter === 'all' && (
        <EmptyState title="No agencies yet" description="When a business registers as an agency in the app, it appears here for review." />
      )}
    </Box>
  );
};

export default Agencies;
